import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { currencySymbol } from '../constants/currency';

import api, { apiErrorMessage, type Student, type Course } from '../services/api';

type TabKey = 'students' | 'courses' | 'groups';

interface ArchivedGroup {
  id: string;
  name?: string;
  course_id?: string;
  course_title?: string;
  days?: string;
  start_time?: string;
  student_count?: number;
}

export default function ArchivePage() {
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  const { theme } = useTheme();
  const curSymbol = currencySymbol(user?.currency);

  // ── State ────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<TabKey>('students');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [groups, setGroups] = useState<ArchivedGroup[]>([]);

  // ── Restoring state ──────────────────────────────────────────────────
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sData, cData, gData] = await Promise.all([
        api.getStudents(undefined, undefined, true),
        api.getCourses(true),
        api.getGroups(undefined, true),
      ]);
      setStudents(sData.students || []);
      setCourses(cData.courses || []);
      setGroups((gData.groups || []).map((g: any) => ({
        ...g,
        student_count: g.student_count || 0,
      })));
    } catch (e) {
      console.error('ArchivePage: Failed to load data:', e);
      setError(e instanceof Error ? e.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Restore handlers ─────────────────────────────────────────────────
  const restoreStudent = async (s: Student) => {
    if (!window.confirm(`Восстановить ученика «${s.first_name} ${s.last_name}»?`)) return;
    setRestoringId(s.id);
    try {
      await api.updateStudent(s.id, { is_active: 'true' });
      setStudents(prev => prev.filter(x => x.id !== s.id));
    } catch (e) {
      console.error('Failed to restore student:', e);
      alert(apiErrorMessage(e, 'Ошибка при восстановлении ученика'));
    } finally {
      setRestoringId(null);
    }
  };

  const restoreCourse = async (c: Course) => {
    if (!window.confirm(`Восстановить курс «${c.title}»?`)) return;
    setRestoringId(c.id);
    try {
      await api.updateCourse(c.id, { is_active: 'true' });
      setCourses(prev => prev.filter(x => x.id !== c.id));
    } catch (e) {
      console.error('Failed to restore course:', e);
      alert(apiErrorMessage(e, 'Ошибка при восстановлении курса'));
    } finally {
      setRestoringId(null);
    }
  };

  const restoreGroup = async (g: ArchivedGroup) => {
    if (!window.confirm(`Восстановить группу «${g.name || 'Без названия'}»?`)) return;
    setRestoringId(g.id);
    try {
      await api.updateGroup(g.id, { is_active: true });
      setGroups(prev => prev.filter(x => x.id !== g.id));
    } catch (e) {
      console.error('Failed to restore group:', e);
      alert(apiErrorMessage(e, 'Ошибка при восстановлении группы'));
    } finally {
      setRestoringId(null);
    }
  };

  // ── Search filter ────────────────────────────────────────────────────
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;
    const q = searchQuery.toLowerCase().trim();
    return students.filter(s =>
      s.first_name.toLowerCase().includes(q) ||
      s.last_name.toLowerCase().includes(q) ||
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
    );
  }, [students, searchQuery]);

  const filteredCourses = useMemo(() => {
    if (!searchQuery.trim()) return courses;
    const q = searchQuery.toLowerCase().trim();
    return courses.filter(c => c.title.toLowerCase().includes(q));
  }, [courses, searchQuery]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase().trim();
    return groups.filter(g =>
      (g.name || '').toLowerCase().includes(q) ||
      (g.course_title || '').toLowerCase().includes(q)
    );
  }, [groups, searchQuery]);

  // ── Tabs config ──────────────────────────────────────────────────────
  const tabs: Array<{ key: TabKey; label: string; icon: React.ReactNode; count: number }> = [
    { key: 'students', label: 'Ученики', icon: <img src="/icons/student-girl_pretty.svg" alt="" width="14" height="14" className="inline object-contain align-middle" />, count: students.length },
    { key: 'courses', label: 'Курсы', icon: <img src="/icons/course.svg" alt="" width="14" height="14" className="inline object-contain align-middle" />, count: courses.length },
    { key: 'groups', label: 'Группы', icon: <img src="/icons/group.svg" alt="" width="14" height="14" className="inline object-contain align-middle" />, count: groups.length },
  ];

  const totalCount = students.length + courses.length + groups.length;

  // ── Loading state ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-4 space-y-4 animate-fade-in">
        <div className="skeleton h-8 w-32 mb-1" />
        <div className="skeleton h-10 w-full rounded-2xl mb-2" />
        <div className="flex gap-2 mb-2">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-10 flex-1 rounded-xl" />)}
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton h-20 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-4 animate-fade-in min-h-full flex flex-col items-center justify-center">
        <div className="tg-card flex flex-col items-center py-12 px-6 text-center max-w-sm mx-auto">
          <span className="text-6xl mb-5">⚠️</span>
          <h2 className="text-lg font-semibold text-[var(--tg-theme-text-color)] mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-[var(--tg-theme-hint-color)] leading-relaxed">
            Не удалось загрузить архив.
            <br />
            Проверьте подключение к серверу.
          </p>
          <details className="w-full mt-4">
            <summary className="text-xs text-[var(--tg-theme-hint-color)] cursor-pointer hover:opacity-70">
              🔧 Техническая информация
            </summary>
            <pre className="mt-2 text-[10px] text-left text-red-500 bg-red-50 rounded-xl p-3 overflow-auto max-h-32">
              {error}
            </pre>
          </details>
          <button onClick={() => navigate(-1)}
            className="mt-6 tg-button text-sm flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Назад
          </button>
          <button onClick={loadData}
            className="mt-3 text-xs text-[var(--tg-theme-button-color)] hover:opacity-70 transition-opacity">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 animate-fade-in pb-24">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-2xl font-bold text-[var(--tg-theme-text-color)]">
            <img src="/icons/archive.svg" alt="" width="24" height="24" className="inline object-contain align-middle" /> Архив
          </h1>
          <p className="text-xs text-[var(--tg-theme-hint-color)] mt-0.5">
            {totalCount} {totalCount === 1 ? 'элемент' : totalCount < 5 ? 'элемента' : 'элементов'} в архиве
          </p>
        </div>
        <button onClick={() => navigate('/school')}
          className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1 hover:opacity-80 transition-opacity">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Назад
        </button>
      </div>

      {/* ── Search ───────────────────────────────────────────────────── */}
      <div className="relative">
        <svg
          className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tg-theme-hint-color)]"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Поиск в архиве..."
          className={`w-full pl-10 pr-4 py-3 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 transition-all placeholder:text-[var(--tg-theme-hint-color)] ${
            theme === 'dark' ? 'bg-white/[0.09]' : 'bg-black/[0.06]'
          }`}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[var(--tg-theme-hint-color)]/20 flex items-center justify-center text-[10px] text-[var(--tg-theme-hint-color)] hover:bg-[var(--tg-theme-hint-color)]/30 transition-colors">
            ✕
          </button>
        )}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <div className="flex gap-1.5">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
              tab === t.key
                ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white shadow-md'
                : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] hover:opacity-80'
            }`}
          >
            <span className="inline-flex items-center gap-1">{t.icon} {t.label}</span>
            <span className={`block mt-0.5 text-[10px] ${tab === t.key ? 'text-white/80' : 'text-[var(--tg-theme-hint-color)]'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* ── Students tab ─────────────────────────────────────────────── */}
      {tab === 'students' && (
        <div className="space-y-2">
          {filteredStudents.map(s => {
            const initials = (s.first_name?.[0] || '') + (s.last_name?.[0] || '');
            return (
              <div key={s.id} className="tg-card flex items-center gap-3.5 p-4">
                <div className="w-12 h-12 rounded-2xl bg-gray-200 text-gray-400 flex items-center justify-center text-base font-bold shrink-0">
                  {s.photo_url ? (
                    <img src={s.photo_url} alt="" className="w-full h-full rounded-2xl object-cover" />
                  ) : (
                    initials || '?'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-[var(--tg-theme-text-color)] block truncate">
                    {s.first_name} {s.last_name}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {s.phone && (
                      <span className="text-[11px] text-[var(--tg-theme-hint-color)]">📞 {s.phone}</span>
                    )}
                    {s.age && (
                      <span className="text-[11px] text-[var(--tg-theme-hint-color)]">🎂 {s.age}</span>
                    )}
                  </div>
                </div>
                {permissions.canEditStudents && (
                  <button
                    onClick={() => restoreStudent(s)}
                    disabled={restoringId === s.id}
                    className="px-3.5 py-2 rounded-xl text-xs font-medium text-green-600 bg-green-50 border border-green-200 hover:bg-green-100 transition-all active:scale-95 disabled:opacity-50 shrink-0"
                  >
{restoringId === s.id ? '⏳...' : <><img src={theme === 'dark' ? '/icons/refresh_dark-theme.svg' : '/icons/refresh_light-theme.svg'} alt="" width="12" height="12" className="inline object-contain align-middle" /> Восстановить</>}
                  </button>
                )}
              </div>
            );
          })}

          {filteredStudents.length === 0 && (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="mb-3"><img src={searchQuery ? '/icons/search.svg' : '/icons/archive.svg'} alt="" width="48" height="48" className="object-contain" /></div>
              <p className="text-sm font-medium text-[var(--tg-theme-text-color)] mb-1">
                {searchQuery ? 'Ничего не найдено' : 'Нет архивированных учеников'}
              </p>
              <p className="text-xs text-[var(--tg-theme-hint-color)]">
                {searchQuery ? 'Попробуйте изменить запрос' : 'В архиве пусто'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Courses tab ──────────────────────────────────────────────── */}
      {tab === 'courses' && (
        <div className="space-y-2">
          {filteredCourses.map(c => (
            <div key={c.id} className="tg-card flex items-center gap-3.5 p-4">
              <div className="w-12 h-12 rounded-2xl bg-gray-200 text-gray-400 flex items-center justify-center text-lg shrink-0">
                📚
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-[var(--tg-theme-text-color)] block truncate">
                  {c.title}
                </span>
                <div className="flex items-center gap-2 mt-0.5">
                  {c.price && (
                    <span className="text-[11px] text-[var(--tg-theme-hint-color)]">
                      <img src="/icons/currency.svg" alt="" width="12" height="12" className="inline object-contain align-middle" /> {parseFloat(c.price).toLocaleString()} {curSymbol}
                    </span>
                  )}
                  {c.duration && (
                    <span className="text-[11px] text-[var(--tg-theme-hint-color)]">⏱ {c.duration} мин</span>
                  )}
                </div>
              </div>
              {permissions.canManageCourses && (
                <button
                  onClick={() => restoreCourse(c)}
                  disabled={restoringId === c.id}
                  className="px-3.5 py-2 rounded-xl text-xs font-medium text-green-600 bg-green-50 border border-green-200 hover:bg-green-100 transition-all active:scale-95 disabled:opacity-50 shrink-0"
                >{restoringId === c.id ? '⏳...' : <><img src={theme === 'dark' ? '/icons/refresh_dark-theme.svg' : '/icons/refresh_light-theme.svg'} alt="" width="12" height="12" className="inline object-contain align-middle" /> Восстановить</>}
                </button>
              )}
            </div>
          ))}

          {filteredCourses.length === 0 && (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="mb-3"><img src={searchQuery ? '/icons/search.svg' : '/icons/archive.svg'} alt="" width="48" height="48" className="object-contain" /></div>
              <p className="text-sm font-medium text-[var(--tg-theme-text-color)] mb-1">
                {searchQuery ? 'Ничего не найдено' : 'Нет архивированных курсов'}
              </p>
              <p className="text-xs text-[var(--tg-theme-hint-color)]">
                {searchQuery ? 'Попробуйте изменить запрос' : 'В архиве пусто'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Groups tab ───────────────────────────────────────────────── */}
      {tab === 'groups' && (
        <div className="space-y-2">
          {filteredGroups.map(g => (
            <div key={g.id} className="tg-card flex items-center gap-3.5 p-4">
              <div className="w-12 h-12 rounded-2xl bg-gray-200 text-gray-400 flex items-center justify-center text-lg shrink-0">
                👥
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-[var(--tg-theme-text-color)] block truncate">
                  {g.name || 'Без названия'}
                </span>
                <div className="flex items-center gap-2 mt-0.5">
                  {g.course_title && (
                    <span className="text-[11px] text-[var(--tg-theme-hint-color)]">📚 {g.course_title}</span>
                  )}
                  {g.days && (
                    <span className="text-[11px] text-[var(--tg-theme-hint-color)]">📅 {g.days}</span>
                  )}
                  {g.start_time && (
                    <span className="text-[11px] text-[var(--tg-theme-hint-color)]">🕐 {g.start_time}</span>
                  )}
                  {g.student_count ? (
                    <span className="text-[11px] text-[var(--tg-theme-hint-color)]">
                      👨‍🎓 {g.student_count}
                    </span>
                  ) : null}
                </div>
              </div>
              {permissions.canManageGroups && (
                <button
                  onClick={() => restoreGroup(g)}
                  disabled={restoringId === g.id}
                  className="px-3.5 py-2 rounded-xl text-xs font-medium text-green-600 bg-green-50 border border-green-200 hover:bg-green-100 transition-all active:scale-95 disabled:opacity-50 shrink-0"
                >{restoringId === g.id ? '⏳...' : <><img src={theme === 'dark' ? '/icons/refresh_dark-theme.svg' : '/icons/refresh_light-theme.svg'} alt="" width="12" height="12" className="inline object-contain align-middle" /> Восстановить</>}
                </button>
              )}
            </div>
          ))}

          {filteredGroups.length === 0 && (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="mb-3"><img src={searchQuery ? '/icons/search.svg' : '/icons/archive.svg'} alt="" width="48" height="48" className="object-contain" /></div>
              <p className="text-sm font-medium text-[var(--tg-theme-text-color)] mb-1">
                {searchQuery ? 'Ничего не найдено' : 'Нет архивированных групп'}
              </p>
              <p className="text-xs text-[var(--tg-theme-hint-color)]">
                {searchQuery ? 'Попробуйте изменить запрос' : 'В архиве пусто'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
